import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  AtSign,
  ChevronRight,
  ContactRound,
  HeartHandshake,
  IdCard,
  MapPin,
  Pencil,
  Phone,
  UsersRound,
} from 'lucide-react';
import { Button, SectionHeader } from '@/ui/primitives';
import { cn } from '@/lib/utils';
import { formatBRL, formatDateBR, formatName } from '@/lib/format';
import { useUpdateCliente } from '../api';
import { formatCpf } from '../data';
import type { ClienteDetail, ClienteUpdateInput } from '../schemas';

interface Props {
  cliente: ClienteDetail;
}

const REGIME_LABEL: Record<string, string> = {
  comunhao_universal: 'Comunhão Universal',
  comunhao_parcial: 'Comunhão Parcial',
  separacao_total: 'Separação Total',
  separacao_obrigatoria: 'Separação Obrigatória',
  participacao_final: 'Participação Final nos Aquestos',
};

const SEXO_OPTIONS: Array<[string, string]> = [
  ['', '— Selecione —'],
  ['M', 'Masculino'],
  ['F', 'Feminino'],
];

const ESTADO_CIVIL_OPTIONS: Array<[string, string]> = [
  ['', '— Selecione —'],
  ['solteiro', 'Solteiro'],
  ['casado', 'Casado'],
  ['divorciado', 'Divorciado'],
  ['uniao_estavel', 'União Estável'],
  ['viuvo', 'Viúvo'],
];

const REGIME_OPTIONS: Array<[string, string]> = [
  ['', '— Selecione —'],
  ['comunhao_universal', 'Comunhão Universal'],
  ['comunhao_parcial', 'Comunhão Parcial'],
  ['separacao_total', 'Separação Total'],
  ['separacao_obrigatoria', 'Separação Obrigatória'],
  ['participacao_final', 'Participação Final nos Aquestos'],
];

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const d = String(raw).replace(/\D/g, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return raw;
}

// solteiro/divorciado: sem cônjuge atual → ocultar.
// casado/uniao_estavel: cônjuge atual → mostrar.
// viuvo: cônjuge falecido mas dados ainda relevantes (inventário, herança) → mostrar.
function showConjuge(estadoCivil: string | null | undefined): boolean {
  if (!estadoCivil) return false;
  // Case-insensitive — banco pode ter casing inconsistente em dados legados.
  return ['casado', 'uniao_estavel', 'viuvo'].includes(String(estadoCivil).trim().toLowerCase());
}

function safeDate(v: string | null | undefined): string {
  if (!v) return '';
  try {
    return formatDateBR(v);
  } catch {
    return v;
  }
}

type EditType = 'text' | 'date' | 'number' | 'select';
type FieldName = keyof ClienteUpdateInput;

interface EditableProps {
  field: FieldName;
  type?: EditType;
  raw: string | number | null | undefined;
  display: ReactNode;
  options?: Array<[string, string]>;
}

interface UpdateCtx {
  cpf: string;
  patch: (data: ClienteUpdateInput) => Promise<void>;
}

const UpdateContext = createContext<UpdateCtx | null>(null);

interface Field {
  label: string;
  edit?: EditableProps;
  staticValue?: ReactNode;
  group?: Array<{ subLabel: string; edit: EditableProps }>;
}

export function ClienteDataView({ cliente }: Props) {
  const c = cliente as ClienteDetail & Record<string, unknown>;
  const update = useUpdateCliente(cliente.cpf);

  const ctx: UpdateCtx = {
    cpf: cliente.cpf,
    patch: (data) =>
      new Promise((resolve, reject) => {
        update.mutate(data, {
          onSuccess: () => resolve(),
          onError: (err) => reject(err),
        });
      }),
  };

  const munUf = c.municipio
    ? `${formatName(c.municipio)}${c.uf ? `/${c.uf}` : ''}`
    : c.uf
      ? String(c.uf)
      : '';

  const certidaoCasamento = c.certidao_casamento_arquivo as string | undefined;

  const identFields: Field[] = [
    { label: 'CPF', staticValue: formatCpf(c.cpf) },
    {
      label: 'Doc Identidade',
      group: [
        {
          subLabel: 'RG',
          edit: { field: 'rg', raw: c.rg, display: c.rg },
        },
        {
          subLabel: 'Órgão',
          edit: { field: 'rg_orgao', raw: c.rg_orgao, display: c.rg_orgao },
        },
        {
          subLabel: 'UF',
          edit: { field: 'rg_uf_emissor', raw: c.rg_uf_emissor, display: c.rg_uf_emissor },
        },
      ],
    },
    {
      label: 'Nascimento',
      edit: {
        field: 'data_nascimento',
        type: 'date',
        raw: c.data_nascimento ? String(c.data_nascimento).slice(0, 10) : '',
        display: safeDate(c.data_nascimento),
      },
    },
    {
      label: 'Sexo',
      edit: {
        field: 'sexo',
        type: 'select',
        raw: c.sexo,
        display: SEXO_OPTIONS.find(([v]) => v === c.sexo)?.[1] ?? c.sexo,
        options: SEXO_OPTIONS,
      },
    },
    {
      label: 'Naturalidade',
      edit: {
        field: 'naturalidade',
        raw: c.naturalidade,
        display: formatName(c.naturalidade),
      },
    },
    {
      label: 'Escolaridade',
      edit: { field: 'escolaridade', raw: c.escolaridade, display: c.escolaridade },
    },
    {
      label: 'Profissão',
      edit: { field: 'profissao', raw: c.profissao, display: formatName(c.profissao) },
    },
    {
      label: 'Renda Bruta Anual',
      edit: {
        field: 'renda_bruta_anual',
        type: 'number',
        raw: c.renda_bruta_anual ?? '',
        display: c.renda_bruta_anual != null ? formatBRL(Number(c.renda_bruta_anual)) : '',
      },
    },
    {
      label: 'Estado Civil',
      edit: {
        field: 'estado_civil',
        type: 'select',
        raw: c.estado_civil,
        display: ESTADO_CIVIL_OPTIONS.find(([v]) => v === c.estado_civil)?.[1] ?? c.estado_civil,
        options: ESTADO_CIVIL_OPTIONS,
      },
    },
  ];

  const enderecoFields: Field[] = [
    { label: 'Logradouro', edit: { field: 'endereco', raw: c.endereco, display: c.endereco } },
    { label: 'Número', edit: { field: 'numero', raw: c.numero, display: c.numero } },
    {
      label: 'Complemento',
      edit: { field: 'complemento', raw: c.complemento, display: c.complemento },
    },
    { label: 'Bairro', edit: { field: 'bairro', raw: c.bairro, display: c.bairro } },
    { label: 'CEP', edit: { field: 'cep', raw: c.cep, display: c.cep } },
    { label: 'Município/UF', staticValue: munUf },
  ];

  const conjugeFields: Field[] = [
    {
      label: 'Regime',
      edit: {
        field: 'regime_casamento',
        type: 'select',
        raw: c.regime_casamento,
        display: REGIME_LABEL[c.regime_casamento ?? ''] ?? c.regime_casamento,
        options: REGIME_OPTIONS,
      },
    },
    {
      label: 'Nome do Cônjuge',
      edit: {
        field: 'conjuge_nome',
        raw: c.conjuge_nome,
        display: formatName(c.conjuge_nome),
      },
    },
    {
      label: 'CPF do Cônjuge',
      edit: {
        field: 'conjuge_cpf',
        raw: c.conjuge_cpf,
        display: c.conjuge_cpf ? formatCpf(c.conjuge_cpf) : '',
      },
    },
    {
      label: 'Naturalidade do Cônjuge',
      edit: {
        field: 'conjuge_naturalidade',
        raw: c.conjuge_naturalidade,
        display: c.conjuge_naturalidade,
      },
    },
    {
      label: 'Nasc. do Cônjuge',
      edit: {
        field: 'conjuge_data_nascimento',
        type: 'date',
        raw: c.conjuge_data_nascimento ? String(c.conjuge_data_nascimento).slice(0, 10) : '',
        display: safeDate(c.conjuge_data_nascimento),
      },
    },
    {
      label: 'Certidão de Casamento',
      staticValue: certidaoCasamento ? (
        <a
          href={certidaoCasamento}
          target="_blank"
          rel="noreferrer"
          className="text-accent hover:underline"
        >
          Ver arquivo
        </a>
      ) : (
        ''
      ),
    },
  ];

  const filiacaoFields: Field[] = [
    {
      label: 'Pai',
      edit: {
        field: 'filiacao_pai',
        raw: c.filiacao_pai,
        display: formatName(c.filiacao_pai),
      },
    },
    {
      label: 'Mãe',
      edit: {
        field: 'filiacao_mae',
        raw: c.filiacao_mae,
        display: formatName(c.filiacao_mae),
      },
    },
    {
      label: 'CPF do Pai',
      edit: {
        field: 'filiacao_pai_cpf',
        raw: c.filiacao_pai_cpf,
        display: c.filiacao_pai_cpf ? formatCpf(c.filiacao_pai_cpf) : '',
      },
    },
    {
      label: 'CPF da Mãe',
      edit: {
        field: 'filiacao_mae_cpf',
        raw: c.filiacao_mae_cpf,
        display: c.filiacao_mae_cpf ? formatCpf(c.filiacao_mae_cpf) : '',
      },
    },
  ];

  return (
    <UpdateContext.Provider value={ctx}>
      <div className="font-ui">
        <SectionHeader icon={ContactRound} title="Contato" className="mt-8" />
        <div className="flex flex-col gap-3">
          <ContactRow
            icon="phone"
            field="telefone"
            raw={c.telefone}
            display={formatPhone(c.telefone)}
            placeholder="Telefone não cadastrado"
          />
          <ContactRow
            icon="email"
            field="email"
            raw={c.email}
            display={c.email ?? ''}
            placeholder="E-mail não cadastrado"
          />
        </div>

        <Accordion icon={IdCard} title="Identificação">
          <FieldsGrid fields={identFields} />
        </Accordion>

        <Accordion icon={MapPin} title="Endereço Residencial">
          <FieldsGrid fields={enderecoFields} />
        </Accordion>

        {/*
          Cônjuge oculto quando solteiro (sem cônjuge) ou divorciado (sem cônjuge atual).
          Visível em casado, união estável e viúvo (dados ainda relevantes para
          inventário, comunhão de bens, herança).
        */}
        {showConjuge(c.estado_civil) && (
          <Accordion icon={HeartHandshake} title="Cônjuge">
            <FieldsGrid fields={conjugeFields} />
          </Accordion>
        )}

        <Accordion icon={UsersRound} title="Filiação">
          <FieldsGrid fields={filiacaoFields} />
        </Accordion>
      </div>
    </UpdateContext.Provider>
  );
}

// Grid de células empilhadas (micro-label em cima, valor embaixo). 4 colunas
// no desktop, 2 no mais estreito.
function FieldsGrid({ fields }: { fields: Field[] }) {
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-5 lg:grid-cols-4">
      {fields.map((f, i) => (
        <StackedField key={i} field={f} />
      ))}
    </div>
  );
}

function StackedField({ field }: { field: Field }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-[11.5px] font-medium text-text-muted">{field.label}</span>
      <div className="min-w-0 break-words text-[15px] font-medium text-text">
        {field.group ? (
          <span className="flex flex-wrap items-baseline gap-x-1">
            {field.group.map((g, i) => (
              <span key={i} className="inline-flex items-baseline whitespace-nowrap">
                {i > 0 && <span className="mr-1 text-text-muted">·</span>}
                <EditableCell {...g.edit} />
              </span>
            ))}
          </span>
        ) : field.edit ? (
          <EditableCell {...field.edit} />
        ) : field.staticValue == null || field.staticValue === '' ? (
          <span className="italic text-text-muted">—</span>
        ) : (
          field.staticValue
        )}
      </div>
    </div>
  );
}

function EditableCell({ field, type = 'text', raw, display, options }: EditableProps) {
  const ctx = useContext(UpdateContext);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState<string>(raw == null ? '' : String(raw));
  const [feedback, setFeedback] = useState<'ok' | 'err' | null>(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null);

  useEffect(() => {
    setValue(raw == null ? '' : String(raw));
  }, [raw]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if ('select' in inputRef.current) inputRef.current.select?.();
    }
  }, [editing]);

  if (!ctx) return <span>{display || '—'}</span>;

  function flash(kind: 'ok' | 'err') {
    setFeedback(kind);
    setTimeout(() => setFeedback(null), kind === 'ok' ? 800 : 1500);
  }

  function save() {
    const original = raw == null ? '' : String(raw);
    if (value === original) {
      setEditing(false);
      return;
    }
    if (!ctx) return;
    const payload =
      type === 'number'
        ? { [field]: value === '' ? null : Number(value) }
        : { [field]: value === '' ? null : value };
    setSaving(true);
    ctx
      .patch(payload as ClienteUpdateInput)
      .then(() => {
        setEditing(false);
        flash('ok');
      })
      .catch(() => flash('err'))
      .finally(() => setSaving(false));
  }

  function cancel() {
    setValue(raw == null ? '' : String(raw));
    setEditing(false);
  }

  const bg =
    feedback === 'ok'
      ? 'rgba(5,150,105,.12)'
      : feedback === 'err'
        ? 'rgba(220,38,38,.12)'
        : undefined;

  // Altura padrão dos campos: 40px (igual Input/Select primitives).
  const fieldClass =
    'h-10 w-full rounded-md bg-elevated border-[1.5px] border-border2 px-3 text-sm text-text ' +
    'transition-colors focus:outline-none focus:border-[var(--accent)]';

  // Modo leitura: valor + botão "Editar" que aparece no hover do campo.
  if (!editing) {
    return (
      <span
        className="group -mx-2 inline-flex min-h-[26px] w-full items-center rounded px-2 transition-colors hover:bg-surface2"
        style={{ background: bg }}
      >
        {display == null || display === '' ? (
          <span className="italic text-text-muted">Pendente</span>
        ) : (
          display
        )}
        <button
          type="button"
          onClick={() => setEditing(true)}
          title="Editar campo"
          aria-label="Editar campo"
          className="ml-auto hidden shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-text-2 transition-colors focus:outline-none group-hover:inline-flex hover:bg-surface3 hover:text-text"
        >
          <Pencil size={12} strokeWidth={1.5} />
          Editar
        </button>
      </span>
    );
  }

  // Modo edição: campo liberado + Salvar/Cancelar abaixo (sem auto-save).
  return (
    <span className="flex w-full flex-col gap-1.5">
      {type === 'select' && options ? (
        <select
          ref={inputRef as React.RefObject<HTMLSelectElement>}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') cancel();
            else if (e.key === 'Enter') {
              e.preventDefault();
              save();
            }
          }}
          className={`${fieldClass} cursor-pointer`}
        >
          {options.map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </select>
      ) : (
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className={fieldClass}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              save();
            } else if (e.key === 'Escape') {
              cancel();
            }
          }}
        />
      )}
      <span className="flex items-center gap-2">
        <Button variant="primary" size="sm" onClick={save} disabled={saving}>
          {saving ? 'Salvando…' : 'Salvar'}
        </Button>
        <Button variant="secondary" size="sm" onClick={cancel} disabled={saving}>
          Cancelar
        </Button>
      </span>
    </span>
  );
}

// Accordion: mesmo cabeçalho de seção (ícone + label + hairline) + chevron
// que gira ao abrir.
function Accordion({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof ContactRound;
  title: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <div
        className="mb-4 mt-8 flex cursor-pointer items-center gap-2.5"
        onClick={() => setOpen((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        <Icon size={14} strokeWidth={1.5} className="shrink-0 text-text-muted" />
        <span className="text-[11px] font-medium uppercase tracking-wide text-text-muted">
          {title}
        </span>
        <span className="h-px flex-1 bg-border" />
        <ChevronRight
          size={16}
          strokeWidth={1.5}
          className={cn('shrink-0 text-text-muted transition-transform', open && 'rotate-90')}
        />
      </div>
      <div className={open ? 'block' : 'hidden'}>{children}</div>
    </div>
  );
}

function ContactRow({
  icon,
  field,
  raw,
  display,
  placeholder,
}: {
  icon: 'phone' | 'email';
  field: 'telefone' | 'email';
  raw: string | null | undefined;
  display: string;
  placeholder: string;
}) {
  const Icon = icon === 'phone' ? Phone : AtSign;
  return (
    <div className="flex items-center gap-3">
      <Icon size={16} strokeWidth={1.5} className="shrink-0 text-text-2" />
      <div className="min-w-0 flex-1 text-[15px] text-text">
        <EditableCell
          field={field}
          raw={raw}
          display={display || <span className="italic text-text-muted">{placeholder}</span>}
        />
      </div>
    </div>
  );
}
